import { useState } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import type { BrReadiness, ReadinessOrder } from '../../hooks/useBusinessReview'
import { formatKpiEur } from '../../lib/formatters'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type RagFilter = 'all' | 'red' | 'amber'
type SortKey = 'value' | 'days' | 'customer'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const RAG_LABEL: Record<ReadinessOrder['rag'], string> = {
  red:   'ROSSO',
  amber: 'AMBRA',
  green: 'VERDE',
}

const RAG_CLASS: Record<ReadinessOrder['rag'], string> = {
  red:   'bg-red-500/15 text-red-400 border-red-500/30',
  amber: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  green: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
}

const RAG_DOT: Record<ReadinessOrder['rag'], string> = {
  red:   'bg-red-400',
  amber: 'bg-amber-400',
  green: 'bg-emerald-400',
}

const STATUS_LABEL: Record<string, string> = {
  confirmed:  'Confermato',
  in_progress: 'In lavorazione',
  delivered:  'Consegnato',
  invoiced:   'Fatturato',
}

function fmtDays(v: number): string {
  return `${v}gg`
}

function fmtCashAtRisk(cashStr: string): string {
  const v = parseFloat(cashStr)
  return v > 0 ? formatKpiEur(v) : 'Regolare'
}

// ---------------------------------------------------------------------------
// Exported pure function — testabile in isolamento
// ---------------------------------------------------------------------------

export function filterAndSort(
  orders: ReadinessOrder[],
  ragFilter: RagFilter,
  sort: SortKey,
): ReadinessOrder[] {
  let result = [...orders]

  if (ragFilter === 'red')   result = result.filter(o => o.rag === 'red')
  if (ragFilter === 'amber') result = result.filter(o => o.rag === 'amber')

  result.sort((a, b) => {
    if (sort === 'value') return parseFloat(b.order_value) - parseFloat(a.order_value)
    if (sort === 'days')  return b.days_no_activity - a.days_no_activity
    return (a.customer_name ?? '').localeCompare(b.customer_name ?? '')
  })

  return result
}

// ---------------------------------------------------------------------------
// RAG summary bar
// ---------------------------------------------------------------------------

interface RagSummaryProps {
  orders: ReadinessOrder[]
  filtered: ReadinessOrder[]
  ragFilter: RagFilter
}

function RagSummary({ orders, filtered, ragFilter }: RagSummaryProps) {
  const byRag = (rag: ReadinessOrder['rag']) => orders.filter(o => o.rag === rag)
  const sumValue = (list: ReadinessOrder[]) =>
    list.reduce((acc, o) => acc + parseFloat(o.order_value), 0)

  const redList   = byRag('red')
  const amberList = byRag('amber')
  const greenList = byRag('green')

  return (
    <div
      data-testid="rag-summary"
      className="flex flex-wrap items-center gap-3 text-xs text-[--km-text-secondary] mb-3"
    >
      <span className={`flex items-center gap-1.5 ${ragFilter === 'red' ? 'font-semibold text-red-400' : ''}`}>
        <span className="w-2 h-2 rounded-full bg-red-400" />
        ROSSO: <strong className="text-[--km-text-primary]">{redList.length}</strong>
        {redList.length > 0 && <span className="text-red-400">— {formatKpiEur(sumValue(redList))}</span>}
      </span>
      <span className="text-[--km-text-disabled]">|</span>
      <span className={`flex items-center gap-1.5 ${ragFilter === 'amber' ? 'font-semibold text-amber-400' : ''}`}>
        <span className="w-2 h-2 rounded-full bg-amber-400" />
        AMBRA: <strong className="text-[--km-text-primary]">{amberList.length}</strong>
        {amberList.length > 0 && <span className="text-amber-400">— {formatKpiEur(sumValue(amberList))}</span>}
      </span>
      <span className="text-[--km-text-disabled]">|</span>
      <span className="flex items-center gap-1.5">
        <span className="w-2 h-2 rounded-full bg-emerald-400" />
        VERDE: <strong className="text-[--km-text-primary]">{greenList.length}</strong>
        {greenList.length > 0 && <span className="text-emerald-400">— {formatKpiEur(sumValue(greenList))}</span>}
      </span>
      {ragFilter !== 'all' && (
        <span className="text-[--km-text-secondary]">
          ({filtered.length} ordini visualizzati)
        </span>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Single order row with drilldown
// ---------------------------------------------------------------------------

function OrderRow({ order }: { order: ReadinessOrder }) {
  const [open, setOpen] = useState(false)

  return (
    <>
      <tr
        data-testid="order-row"
        onClick={() => setOpen(v => !v)}
        className="cursor-pointer border-b border-[var(--km-card-border)] hover:bg-white/[0.03] transition-colors"
      >
        <td className="py-2.5 px-3 text-xs text-[--km-text-primary] font-medium">
          {order.customer_name || '—'}
        </td>
        <td className="py-2.5 px-3 text-xs text-[--km-text-secondary]">
          {STATUS_LABEL[order.status] ?? order.status}
        </td>
        <td className="py-2.5 px-3">
          <span
            data-testid={`rag-badge-${order.rag}`}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-medium ${RAG_CLASS[order.rag]}`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${RAG_DOT[order.rag]}`} />
            {RAG_LABEL[order.rag]}
          </span>
        </td>
        <td className="py-2.5 px-3 text-xs text-[--km-text-secondary] text-right">
          {fmtDays(order.days_no_activity)}
        </td>
        <td className="py-2.5 px-3 text-xs text-[--km-text-primary] text-right font-medium">
          {formatKpiEur(parseFloat(order.order_value))}
        </td>
        <td className="py-2.5 px-3 text-xs text-right">
          <span className={parseFloat(order.cash_at_risk) > 0 ? 'text-red-400 font-medium' : 'text-[--km-text-secondary]'}>
            {fmtCashAtRisk(order.cash_at_risk)}
          </span>
        </td>
        <td className="py-2.5 px-3 text-[--km-text-secondary]">
          {open
            ? <ChevronUp className="w-3.5 h-3.5" />
            : <ChevronDown className="w-3.5 h-3.5" />
          }
        </td>
      </tr>

      {open && (
        <tr data-testid="order-drilldown">
          <td colSpan={7} className="px-4 py-3 bg-[var(--km-bg-surface)]/50">
            <div className="rounded-xl border border-[var(--km-card-border)] bg-[var(--km-card-bg)] px-4 py-3 text-xs space-y-1.5">
              {order.blocco ? (
                <>
                  <p className="text-[--km-text-secondary]">
                    <span className="font-semibold text-[--km-text-primary]">Causa: </span>
                    {order.blocco}
                  </p>
                  {parseFloat(order.cash_at_risk) > 0 && (
                    <p className="text-[--km-text-secondary]">
                      <span className="font-semibold text-red-400">Impatto: </span>
                      {formatKpiEur(parseFloat(order.cash_at_risk))} di revenue a rischio di slittamento
                    </p>
                  )}
                  <p className="text-[--km-text-secondary] italic">
                    Azione suggerita: Verificare avanzamento con PM — aggiornare stato produzione
                  </p>
                </>
              ) : (
                <p className="text-emerald-400">Ordine in regola — nessun blocco rilevato.</p>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// ReadinessTable
// ---------------------------------------------------------------------------

interface ReadinessTableProps {
  readiness: BrReadiness
}

export function ReadinessTable({ readiness }: ReadinessTableProps) {
  const [ragFilter, setRagFilter] = useState<RagFilter>('all')
  const [sort, setSort] = useState<SortKey>('value')

  const { orders } = readiness
  const filtered = filterAndSort(orders, ragFilter, sort)

  if (!orders.length) {
    return (
      <div
        data-testid="readiness-empty"
        className="mt-4 flex items-center justify-center h-24 rounded-xl border border-dashed border-[var(--km-card-border)] text-xs text-[--km-text-secondary]"
      >
        Nessun ordine backlog nel periodo selezionato
      </div>
    )
  }

  return (
    <div data-testid="readiness-table" className="mt-4 space-y-3">
      {/* Sommario RAG */}
      <RagSummary orders={orders} filtered={filtered} ragFilter={ragFilter} />

      {/* Filtri + sort */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Filtro RAG */}
        <div className="flex items-center gap-1 rounded-xl border border-[var(--km-card-border)] bg-[var(--km-card-bg)] p-1">
          {(['all', 'red', 'amber'] as RagFilter[]).map(f => (
            <button
              key={f}
              type="button"
              data-testid={`filter-${f}`}
              onClick={() => setRagFilter(f)}
              className={`px-2.5 py-1 rounded-lg text-[10px] font-medium transition-colors ${
                ragFilter === f
                  ? 'bg-indigo-500/20 text-indigo-300'
                  : 'text-[--km-text-secondary] hover:text-[--km-text-primary]'
              }`}
            >
              {f === 'all' ? 'Tutti' : f === 'red' ? 'Solo Rosso' : 'Solo Ambra'}
            </button>
          ))}
        </div>

        {/* Sort */}
        <div className="rounded-xl border border-[var(--km-card-border)] bg-[var(--km-card-bg)] px-2.5 py-1.5">
          <label htmlFor="readiness-sort" className="sr-only">Ordina per</label>
          <select
            id="readiness-sort"
            value={sort}
            onChange={e => setSort(e.target.value as SortKey)}
            className="bg-transparent text-[--km-text-primary] text-[10px] focus:outline-none"
            aria-label="Ordina per"
          >
            <option value="value">Per valore</option>
            <option value="days">Per gg senza attività</option>
            <option value="customer">Per cliente</option>
          </select>
        </div>
      </div>

      {/* Tabella */}
      <div className="overflow-x-auto rounded-xl border border-[var(--km-card-border)]">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-[var(--km-card-border)] bg-white/[0.02]">
              <th className="py-2 px-3 text-[10px] font-semibold uppercase tracking-wide text-[--km-text-secondary]">Cliente</th>
              <th className="py-2 px-3 text-[10px] font-semibold uppercase tracking-wide text-[--km-text-secondary]">Status</th>
              <th className="py-2 px-3 text-[10px] font-semibold uppercase tracking-wide text-[--km-text-secondary]">RAG</th>
              <th className="py-2 px-3 text-[10px] font-semibold uppercase tracking-wide text-[--km-text-secondary] text-right">Gg inattività</th>
              <th className="py-2 px-3 text-[10px] font-semibold uppercase tracking-wide text-[--km-text-secondary] text-right">Valore</th>
              <th className="py-2 px-3 text-[10px] font-semibold uppercase tracking-wide text-[--km-text-secondary] text-right">Cash a rischio</th>
              <th className="py-2 px-3 w-8" />
            </tr>
          </thead>
          <tbody>
            {filtered.map(order => (
              <OrderRow key={order.id} order={order} />
            ))}
            {filtered.length === 0 && (
              <tr>
                <td
                  colSpan={7}
                  data-testid="readiness-filter-empty"
                  className="py-6 text-center text-xs text-[--km-text-secondary]"
                >
                  Nessun ordine corrisponde al filtro selezionato
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
